import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Search, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

interface FAQItem {
    question: string;
    answer: string;
    category: string;
}

const FAQPage_es: React.FC = () => {
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    const faqData: FAQItem[] = [
        // Configuración Inicial
        {
            category: 'Configuración Inicial',
            question: '¿Cómo empiezo a usar el sistema?',
            answer: 'Para empezar, primero necesitas conectar una instancia de WhatsApp y configurar tu SERP API. Después, configura tus agentes de IA en la sección de Inteligencia Artificial. Por último, estarás listo para hacer búsquedas en Google Maps y encontrar clientes potenciales.'
        },
        {
            category: 'Configuración Inicial',
            question: '¿Es necesario tener conocimientos técnicos para usar el sistema?',
            answer: '¡No! El sistema fue desarrollado para ser intuitivo y fácil de usar. Solo necesitas seguir el paso a paso de conexión del WhatsApp y de la SERP API. Todo lo demás se hace a través de interfaces amigables.'
        },

        // WhatsApp y SERP API
        {
            category: 'WhatsApp y SERP API',
            question: '¿Cómo conecto mi instancia de WhatsApp?',
            answer: 'En la página de Integraciones, haz clic en "Conectar WhatsApp" y escanea el código QR con tu celular. En pocos segundos tu instancia estará conectada y lista para usar. Asegúrate de mantener tu celular conectado a internet.'
        },
        {
            category: 'WhatsApp y SERP API',
            question: '¿Qué es la SERP API y por qué la necesito?',
            answer: 'La SERP API permite que el sistema haga búsquedas automatizadas en Google Maps para encontrar leads (clientes potenciales) basados en los criterios que definas. Es esencial para el funcionamiento de la búsqueda de leads.'
        },
        {
            category: 'WhatsApp y SERP API',
            question: 'Mi WhatsApp se desconecta con frecuencia. ¿Qué hacer?',
            answer: 'Asegúrate de que tu celular tiene internet estable y que WhatsApp no se está usando en otro dispositivo. Si el problema persiste, intenta reconectar la instancia en la página de Integraciones.'
        },

        // Agentes de IA
        {
            category: 'Agentes de IA',
            question: '¿Cuál es la diferencia entre Agente de Envío y Agente de Atención?',
            answer: 'El Agente de Envío envía mensajes automáticos iniciales a los leads encontrados. El Agente de Atención (disponible en el Plan Pro) responde automáticamente a los mensajes recibidos, manteniendo conversaciones inteligentes con tus clientes potenciales.'
        },
        {
            category: 'Agentes de IA',
            question: '¿Cómo configuro mi agente de envío?',
            answer: 'Ve a la sección de IA, haz clic en "Agente de Envío" y personaliza el mensaje que se enviará, el tono de voz y la información sobre tu negocio. El agente usará esta información para crear mensajes personalizados.'
        },
        {
            category: 'Agentes de IA',
            question: '¿El Agente de Atención está disponible en mi plan?',
            answer: 'El Agente de Atención es una funcionalidad exclusiva del Plan Pro. Si estás en el plan básico o gratuito, considera hacer upgrade para tener acceso a esta poderosa herramienta.'
        },

        // Búsqueda y Filtros
        {
            category: 'Búsqueda y Filtros',
            question: '¿Cómo hago una búsqueda eficiente en Google Maps?',
            answer: 'Usa términos específicos relacionados con el tipo de negocio que buscas (ej: "restaurantes", "gimnasios", "salones de belleza") y define la ubicación deseada. Cuanto más específico, mejores serán los resultados.'
        },
        {
            category: 'Búsqueda y Filtros',
            question: '¿Qué filtros puedo aplicar en los resultados?',
            answer: 'Puedes filtrar por valoración, número de reseñas, estado de WhatsApp (verificado, no verificado, inválido) y otros criterios. Esto ayuda a enfocarte solo en los leads más relevantes para tu negocio.'
        },
        {
            category: 'Búsqueda y Filtros',
            question: '¿Cómo sé si un lead tiene WhatsApp válido?',
            answer: 'El sistema verifica automáticamente los números de WhatsApp encontrados e indica el estado: "Válido" (el número existe en WhatsApp), "Inválido" (el número no existe), o "Pendiente" (verificación en curso).'
        },

        // Planes y Funcionalidades
        {
            category: 'Planes y Funcionalidades',
            question: '¿Cuáles son las diferencias entre los planes?',
            answer: 'El Plan Gratuito ofrece funcionalidades básicas limitadas. El Plan Básico elimina algunas limitaciones y añade más búsquedas. El Plan Pro incluye el Agente de Atención automático, búsquedas ilimitadas y soporte prioritario.'
        },
        {
            category: 'Planes y Funcionalidades',
            question: '¿Cómo hago upgrade de mi plan?',
            answer: 'Contacta a través de la página de Ayuda o accede al enlace de upgrade disponible en la página de Perfil. Nuestro equipo te ayudará en el proceso.'
        },
        {
            category: 'Planes y Funcionalidades',
            question: '¿Puedo cancelar mi suscripción en cualquier momento?',
            answer: '¡Sí! Puedes cancelar tu suscripción cuando quieras a través de Hotmart. Después de la cancelación, aún tendrás acceso hasta el fin del período ya pagado.'
        }
    ];

    const categories = Array.from(new Set(faqData.map(item => item.category)));

    const filteredFAQ = faqData.filter(item =>
        item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.answer.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="p-6 lg:p-10 max-w-4xl mx-auto flex flex-col gap-8"
        >
            <div>
                <Link to="/documentacao" className="inline-flex items-center gap-2 text-primary hover:underline mb-4">
                    <ArrowLeft className="size-4" />
                    Volver a Documentación
                </Link>

                <motion.h1
                    className="text-4xl font-black leading-tight tracking-[-0.033em] text-transparent bg-clip-text bg-gradient-primary mb-2"
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1, duration: 0.6 }}
                >
                    Preguntas Frecuentes
                </motion.h1>
                <p className="text-lg text-gray-600 dark:text-gray-400">
                    Encuentra respuestas rápidas a tus dudas
                </p>
            </div>

            {/* Search Bar */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="relative"
            >
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 size-5 text-gray-400" />
                <input
                    type="text"
                    placeholder="Buscar pregunta..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full h-12 pl-12 pr-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-[#181411] dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
                />
            </motion.div>

            {/* FAQ Items by Category */}
            <div className="flex flex-col gap-8">
                {categories.map((category, catIndex) => {
                    const categoryItems = filteredFAQ.filter(item => item.category === category);

                    if (categoryItems.length === 0) return null;

                    return (
                        <motion.div
                            key={category}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 + catIndex * 0.1 }}
                        >
                            <h2 className="text-2xl font-bold text-[#181411] dark:text-white mb-4">
                                {category}
                            </h2>

                            <div className="flex flex-col gap-3">
                                {categoryItems.map((item, index) => {
                                    const globalIndex = faqData.indexOf(item);
                                    const isOpen = openIndex === globalIndex;

                                    return (
                                        <motion.div
                                            key={globalIndex}
                                            className="card overflow-hidden"
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: 0.4 + index * 0.05 }}
                                        >
                                            <button
                                                onClick={() => setOpenIndex(isOpen ? null : globalIndex)}
                                                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                                            >
                                                <span className="font-semibold text-[#181411] dark:text-white pr-4">
                                                    {item.question}
                                                </span>
                                                <motion.div
                                                    animate={{ rotate: isOpen ? 180 : 0 }}
                                                    transition={{ duration: 0.2 }}
                                                >
                                                    <ChevronDown className="size-5 text-gray-500 shrink-0" />
                                                </motion.div>
                                            </button>

                                            {isOpen && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    transition={{ duration: 0.2 }}
                                                >
                                                    <div className="px-4 pb-4 text-gray-600 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700/50 pt-4">
                                                        {item.answer}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </motion.div>
                                    );
                                })}
                            </div>
                        </motion.div>
                    );
                })}
            </div>

            {filteredFAQ.length === 0 && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="card p-8 text-center"
                >
                    <p className="text-gray-500 dark:text-gray-400">
                        No se encontraron preguntas para "{searchTerm}"
                    </p>
                </motion.div>
            )}
        </motion.div>
    );
};

export default FAQPage_es;
